export const RETURN_POLICY_META = {
  title: "Pickle Bullpen Pro Shop — Return, Refund & Exchange Policy",
  effective: "June 1, 2026",
  company: "The Pickle Bullpin LLC",
  contact: "admin@picklebullpen.com",
  phone: "941-899-5300",
  windowDays: 30,
  url: "https://www.picklebullpen.com/return-policy",
};

export const RETURN_POLICY_SUMMARY = [
  "30-day return window from the delivery date for unused, unopened items in original packaging.",
  "Defective or wrong items: we cover return shipping and send a replacement or full refund.",
  "Change-of-mind returns: customer pays return shipping; refund to the original payment method (or BLC wallet for coin purchases).",
  "Final sale: used/opened balls, worn apparel, customized or personalized items, gift cards, clearance items and digital goods.",
  "Start every return with a Return Authorization (RA) number — unauthorized returns are refused.",
];

export const RETURN_POLICY_SECTIONS = [
  {
    h: "1. Scope",
    p: [
      `This policy applies to all physical goods purchased from the Pickle Bullpen Pro Shop (the "Shop") at picklebullpen.com, including purchases made with a credit/debit card through Stripe and purchases made with BULLION Coin ("BLC"). It does not apply to items sold through third-party "Buy Now" ambassador links, which are governed by that seller's policy, nor to tournament entries, memberships, session passes, lessons, or other services, which are governed by their own terms at the point of sale.`,
    ],
  },
  {
    h: "2. Return Window",
    p: [
      `You may request a return within thirty (30) calendar days of the carrier-confirmed delivery date. Requests received after day 30 will be declined, except where required by applicable law or where the item is defective and covered under Section 5.`,
      `For local pickup orders, the 30-day window begins on the date the order is marked collected.`,
    ],
  },
  {
    h: "3. Eligibility — Items Must Be",
    list: [
      "Unused, unworn and unwashed, with all original tags, labels, hang-tags and protective films intact;",
      "In the original, undamaged manufacturer packaging with all accessories, manuals, inserts and free promotional items included;",
      "Free of court marks, scuffs, sweat, odors, pet hair, or any sign of play or wear;",
      "Accompanied by the order number and a valid Return Authorization (RA) number issued by us.",
    ],
    p: [
      `Items that do not meet every condition above may be refused, or accepted subject to a restocking deduction of up to 25% of the item price, at our sole discretion. Refused items will be returned to you at your expense or, if unclaimed within 14 days of notice, disposed of.`,
    ],
  },
  {
    h: "4. Final Sale — Not Returnable",
    list: [
      "Pickleballs once the pack seal has been opened (hygiene and playability cannot be verified);",
      "Overgrips, replacement grips, grip tape and edge-guard tape once the packaging has been opened;",
      "Apparel and headwear that has been worn or washed, and all undergarments, socks and compression wear;",
      "Custom, personalized, engraved, or team-ordered items;",
      "Items marked Clearance, Final Sale, Open Box, or sold at a discount of 40% or more;",
      "Gift cards, BLC coin loads, digital products, downloads and training content;",
      "Items damaged through misuse, improper storage, exposure to extreme heat/cold, or modification.",
    ],
  },
  {
    h: "5. Defective, Damaged-in-Transit or Incorrect Items",
    p: [
      `If an item arrives damaged, defective, or is not what you ordered, notify us within seven (7) days of delivery with your order number and clear photographs of the item, the packaging, and the shipping label. We will issue a prepaid return label and, at your choice, ship a replacement of the same item or issue a full refund including original shipping charges once the item is received or the carrier scan confirms it is in transit back to us.`,
      `Manufacturer defects discovered after 7 days but within the 30-day window are handled under this section; defects discovered after 30 days must be pursued under the manufacturer's warranty, and we will gladly provide the manufacturer's contact details and your proof of purchase.`,
      `Paddle face delamination, core crushing, or edge-guard separation caused by normal play within 30 days is treated as a defect. Cosmetic marks from play, chipped paint, or damage from hitting fences, posts or the ground are not defects.`,
    ],
  },
  {
    h: "6. How to Start a Return (Required)",
    list: [
      `Email ${RETURN_POLICY_META.contact} or text ${RETURN_POLICY_META.phone} with your order number, the item(s), the reason, and photos if the item is damaged or defective.`,
      "We will reply within two (2) business days with a Return Authorization (RA) number and return instructions.",
      "Write the RA number clearly on the outside of the package and include your packing slip or order number inside.",
      "Ship within 10 days of receiving the RA number using a tracked, insured service. Returns without an RA number or without tracking are refused and we are not responsible for lost packages.",
    ],
  },
  {
    h: "7. Return Shipping Costs",
    p: [
      `Defective, damaged or incorrect items: we pay. Change-of-mind, wrong size/color chosen by the customer, or "no longer needed" returns: the customer pays return shipping and the original outbound shipping charge is non-refundable. Where an order qualified for free shipping and the return drops the kept items below the free-shipping threshold, the standard shipping charge will be deducted from the refund.`,
    ],
  },
  {
    h: "8. Refunds",
    p: [
      `Approved refunds are issued within five (5) business days after the returned item is received and inspected. Card purchases are refunded to the original card via Stripe; your bank may take an additional 5–10 business days to post the credit. BLC purchases are refunded as BLC to the wallet that paid; BLC is never redeemable for cash. Sales tax collected on the returned item is refunded; service fees, processing fees and shipping charges are refunded only for returns under Section 5.`,
      `If the original payment method is unavailable, refunds will be issued as Shop store credit. Refunds are made only to the original purchaser.`,
    ],
  },
  {
    h: "9. Exchanges",
    p: [
      `We exchange for a different size, color or pack option of the same item when stock allows. Request the exchange when you obtain your RA number. To receive the new item fastest, place a new order and return the original for a refund. Price differences are charged or refunded accordingly.`,
    ],
  },
  {
    h: "10. Cancellations",
    p: [
      `Orders may be cancelled for a full refund until they are marked Shipped in your account. Once shipped, the order is subject to this return policy. Custom and personalized orders cannot be cancelled once production has begun.`,
    ],
  },
  {
    h: "11. Abuse & Chargebacks",
    p: [
      `We reserve the right to refuse returns, limit exchanges, or close accounts that show a pattern of excessive returns, returned items that were used, or returns of items that do not match what was shipped (verified by serial, lot, or photo records taken at packing). Filing a chargeback while a return or refund is in progress may result in the refund being withheld pending the card network's resolution.`,
    ],
  },
  {
    h: "12. Your Statutory Rights & Changes to This Policy",
    p: [
      `Nothing in this policy limits rights you have under applicable consumer-protection law, including Florida law. We may update this policy at any time; the version posted at ${RETURN_POLICY_META.url} on the date of your order governs that order. Questions: ${RETURN_POLICY_META.contact} · ${RETURN_POLICY_META.phone}.`,
    ],
  },
];
